export interface CatalogVariantMatch {
  variantLabel: string;
  volumeMl: number | null;
}

export interface ParsedCatalogProductText extends CatalogVariantMatch {
  name: string;
}

export const CATALOG_VARIANT_VOLUME_ML: Record<string, number> = {
  REGULAR: 330,
  '1/4': 250,
  '250ML': 250,
  '350ML': 350,
  '1/2': 500,
  '500ML': 500,
  '750ML': 750,
  '1 LITER': 1000,
  '2 LITER': 2000,
  '5 LITER': 5000,
  CAN: 330,
  GLASS: 500,
  BOTTLE: 500,
  SMALL: 500,
  BIG: 1000,
};

const PREFIX_VARIANT_PATTERNS: Array<{
  regex: RegExp;
  variantLabel: string;
}> = [
  { regex: /^(5\s*L(?:ITER|ITRE)?)\s+(.+)$/i, variantLabel: '5 LITER' },
  { regex: /^(2\s*L(?:ITER|ITRE)?)\s+(.+)$/i, variantLabel: '2 LITER' },
  { regex: /^(1\s*L(?:ITER|ITRE|TR)?)\s+(.+)$/i, variantLabel: '1 LITER' },
];

const SUFFIX_VARIANT_PATTERNS: Array<{
  regex: RegExp;
  variantLabel: string;
}> = [
  { regex: /^(.+?)\s+(5\s*L(?:ITER|ITRE)?)$/i, variantLabel: '5 LITER' },
  { regex: /^(.+?)\s+(5000ML)$/i, variantLabel: '5 LITER' },
  { regex: /^(.+?)\s+(2\s*L(?:ITER|ITRE)?)$/i, variantLabel: '2 LITER' },
  { regex: /^(.+?)\s+(2000ML)$/i, variantLabel: '2 LITER' },
  { regex: /^(.+?)\s+(1\s*L(?:ITER|ITRE|TR)?)$/i, variantLabel: '1 LITER' },
  { regex: /^(.+?)\s+(1000ML)$/i, variantLabel: '1 LITER' },
  { regex: /^(.+?)\s+(750ML|750L)$/i, variantLabel: '750ML' },
  { regex: /^(.+?)\s+(500ML)$/i, variantLabel: '500ML' },
  { regex: /^(.+?)\s+(350ML)$/i, variantLabel: '350ML' },
  { regex: /^(.+?)\s+(330ML)$/i, variantLabel: 'CAN' },
  { regex: /^(.+?)\s+(250ML)$/i, variantLabel: '250ML' },
  { regex: /^(.+?)\s+(1\/2)$/i, variantLabel: '1/2' },
  { regex: /^(.+?)\s+(1\/4)$/i, variantLabel: '1/4' },
  { regex: /^(.+?)\s+(CAN)$/i, variantLabel: 'CAN' },
  { regex: /^(.+?)\s+(GLASS)$/i, variantLabel: 'GLASS' },
  { regex: /^(.+?)\s+(BIG)$/i, variantLabel: 'BIG' },
  { regex: /^(.+?)\s+(SMALL)$/i, variantLabel: 'SMALL' },
  { regex: /^(.+?)\s+(16%)$/i, variantLabel: '16%' },
  { regex: /^(.+?)\s+(12%)$/i, variantLabel: '12%' },
  { regex: /^(.+?)\s+(BOTTLE)$/i, variantLabel: 'BOTTLE' },
  { regex: /^(.+?)\s+(B)$/i, variantLabel: 'BOTTLE' },
];

export function normalizeCatalogLine(value: string): string {
  return value
    .replace(/Â½/g, '1/2')
    .replace(/Â¼/g, '1/4')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

export function canonicalizeCatalogProductName(value: string): string {
  return normalizeCatalogLine(value)
    .replace(/\bTRIPPLE\b/g, 'TRIPLE')
    .replace(/\bPLISNER\b/g, 'PILSNER')
    .replace(/\bPEAL\b/g, 'PEARL')
    .replace(/\bSEANSONS\b/g, 'SEASONS')
    .replace(/\bALL SEASON\b/g, 'ALL SEASONS')
    .replace(/\bW\/CAP\b/g, 'WHITE CAP')
    .replace(/\bR\. LABEL\b/g, 'RED LABEL')
    .replace(/\bB\. LABEL\b/g, 'BLACK LABEL')
    .replace(/\bJOHN WALKER\b/g, 'JOHNNY WALKER')
    .replace(/\bJOHNNIE WALKER\b/g, 'JOHNNY WALKER')
    .replace(/\bV\/A\b/g, 'VICEROY')
    .replace(/\bV&A\b/g, 'VICEROY')
    .replace(/\bVICEROY BRANDY\b/g, 'VICEROY')
    .replace(/\bCOUNTY\b$/g, 'COUNTY BRANDY')
    .replace(/\bBEST\b$/g, 'BEST WHISKEY')
    .replace(/\bCARPRICE\b/g, 'CAPRICE')
    .replace(/\bCELLER\b/g, 'CELLAR')
    .replace(/\bDESPARADO\b/g, 'DESPERADOS')
    .replace(/\bG\.\s*MEAKINS\b/g, 'G.MEAKINS')
    .replace(/\bG\.K\b/g, 'GK')
    .replace(/\bGORDON'?S?\b/g, 'GORDON')
    .replace(/\bGUARRANA\b/g, 'GUARANA')
    .replace(/\bK\.C\b/g, 'KC')
    .replace(/\bK\.KING\b/g, 'KING')
    .replace(/\bNAPPS\b/g, 'NAPS')
    .replace(/\bWILLIAM LAWSON'?S?\b/g, 'WILLIAMLAWSON')
    .replace(/\bBOUMD\b/g, 'BOND');
}

export function normalizeCatalogVariantLabel(value: string): string {
  return normalizeCatalogLine(value)
    .replace(/^1LTR$/g, '1 LITER')
    .replace(/^1 LTR$/g, '1 LITER')
    .replace(/^1 LITRE$/g, '1 LITER')
    .replace(/^1000ML$/g, '1 LITER')
    .replace(/^2 LITRE$/g, '2 LITER')
    .replace(/^2000ML$/g, '2 LITER')
    .replace(/^5 LITRE$/g, '5 LITER')
    .replace(/^5000ML$/g, '5 LITER')
    .replace(/^750L$/g, '750ML')
    .replace(/^330ML$/g, 'CAN')
    .replace(/^B$/g, 'BOTTLE');
}

export function getCatalogVariantVolumeMl(variantLabel: string | null | undefined): number | null {
  if (!variantLabel) {
    return null;
  }

  return CATALOG_VARIANT_VOLUME_ML[normalizeCatalogVariantLabel(variantLabel)] ?? null;
}

export function parseCatalogProductText(value: string): ParsedCatalogProductText {
  const normalizedValue = normalizeCatalogLine(value);

  for (const pattern of PREFIX_VARIANT_PATTERNS) {
    const match = normalizedValue.match(pattern.regex);
    if (match) {
      const nested = parseCatalogProductText(match[2] ?? normalizedValue);
      return {
        name: nested.name,
        variantLabel: pattern.variantLabel,
        volumeMl: getCatalogVariantVolumeMl(pattern.variantLabel),
      };
    }
  }

  for (const pattern of SUFFIX_VARIANT_PATTERNS) {
    const match = normalizedValue.match(pattern.regex);
    if (match) {
      const nested = parseCatalogProductText(match[1] ?? normalizedValue);
      return {
        name: nested.name,
        variantLabel: pattern.variantLabel,
        volumeMl: getCatalogVariantVolumeMl(pattern.variantLabel),
      };
    }
  }

  return {
    name: canonicalizeCatalogProductName(normalizedValue),
    variantLabel: 'REGULAR',
    volumeMl: null,
  };
}

export function getCatalogLookupKey(value: string): string {
  return canonicalizeCatalogProductName(value).replace(/[^A-Z0-9]+/g, '');
}
